import type { TFunction } from 'i18next';
import type { SourcePostFilters } from '@/hooks/useSourcePosts';
import { fromSavedFiltersDoc, toSavedFiltersDoc } from '@/lib/savedSearchDoc';
import { kluczWyszukiwania } from '@/lib/historiaWyszukiwan';
import { etykietaCenyZ, formatRange } from '@/lib/serpFormat';
import { etykietaWyboruPokoi } from '@/lib/pokoje';
import { etykietaObszaru } from '@/lib/areaSearch';
import { PRIMARY_ACTIVE_CITY } from '@/lib/geoScope';
import { formatLiczby } from '@/lib/i18nNumber';

/**
 * MOJE FILTRY NA STRONIE WYNIKÓW — REGUŁY FLAGI I „ZAPISZ” (plan E1.3, §1.2–1.9, §1.71; build plan C1).
 * Czyste funkcje, bez Reacta i bez Supabase: hook `useMojeFiltry` i komponent
 * `ZakladkaMojichFiltrow` tylko je składają. Makiety: `docs/wireframes/serp-saved-filters-proto*.html`.
 *
 *  - ZAPALONA flaga = to, co na ekranie, RÓWNA SIĘ zapisanemu zbiorowi (dzielnice też). Przed
 *    pierwszym zapisem (`savedAt` null, §1.71) flaga nie świeci nigdy: wypełnienie z onboardingu
 *    to jeszcze nie Moje filtry.
 *  - „ZAPISZ” widać zawsze, dopóki nic nie zapisano; po pierwszym zapisie tylko po EDYCJI, która
 *    rozjechała ekran ze zbiorem. Wyłączenie flagi i × to nie edycja (§1.3).
 *  - Zapis wymaga ceny (§1.3, lustro `flat.saved_filters_has_price`); bez niej „Zapisz” jest szare.
 *  - Przed pierwszym zapisem flaga i „Zapisz” to JEDEN przycisk (dotknięcie flagi zapisuje); po
 *    pierwszym zapisie flaga przy wysuniętym „Zapisz” znaczy „nie zapisuj, wróć do Moich filtrów”.
 */

/**
 * Stan filtrów tak, jak zapisałby go dokument: soczewki debug, `view`, „Ulubione” i inne pola spoza
 * zbioru nie tworzą różnicy, `suburbs` liczą się jak dzielnice, a domyślne typy ogłoszeń
 * (`DEFAULT_FILTERS`) jak ich brak — tak samo jak w adresie.
 */
export function kluczZbioru(f: SourcePostFilters): string {
  return kluczWyszukiwania(fromSavedFiltersDoc(toSavedFiltersDoc(f)));
}

export function rowneZbiorowi(a: SourcePostFilters, b: SourcePostFilters): boolean {
  return kluczZbioru(a) === kluczZbioru(b);
}

/** Czy zbiór wolno ZAPISAĆ (§1.3: miasto + limit ceny). Lustro `flat.saved_filters_has_price`. */
export function maCene(f: SourcePostFilters): boolean {
  return (f.rentMax ?? 0) > 0 || (f.rentMin ?? 0) > 0;
}

export interface WejscieFlagi {
  /** Zapisany zbiór — `null`, gdy jeszcze nic nie zapisano (§1.71). */
  zapisane: SourcePostFilters | null;
  /** Filtry na ekranie. */
  ekran: SourcePostFilters;
  /** Czy ekran różni się od zbioru z powodu ZMIANY człowieka (a nie wyłączenia flagi albo ×). */
  edytowano: boolean;
}

export interface StanFlagi {
  /** Jest zapisany zbiór (Moje filtry istnieją). */
  maZbior: boolean;
  /** Ekran = zbiór. */
  zapalona: boolean;
  /** „Zapisz” wysunięte. */
  pokazZapisz: boolean;
  /** Na ekranie jest cena — „Zapisz” aktywne. */
  mozeZapisac: boolean;
  /** Przed pierwszym zapisem: flaga i „Zapisz” to jeden przycisk, bez kreski. */
  jedenPrzycisk: boolean;
}

export function stanFlagi({ zapisane, ekran, edytowano }: WejscieFlagi): StanFlagi {
  const maZbior = zapisane !== null;
  const zapalona = maZbior && rowneZbiorowi(ekran, zapisane);
  return {
    maZbior,
    zapalona,
    pokazZapisz: !maZbior || (!zapalona && edytowano),
    mozeZapisac: maCene(ekran),
    jedenPrzycisk: !maZbior,
  };
}

/**
 * Co robi dotknięcie FLAGI:
 *  - `zapisz` — przed pierwszym zapisem flaga jest częścią przycisku „Zapisz”;
 *  - `wylacz` — zapalona: czyści wszystko, lokalizację też (§1.69), jak ×;
 *  - `odrzuc` — zgaszona przy wysuniętym „Zapisz”: „nie zapisuj, wróć do Moich filtrów” —
 *    „Zapisz” przekreśla się i chowa, potem ekran = zbiór;
 *  - `wlacz` — zgaszona bez „Zapisz”: wczytuje zbiór od razu, bez „Pokaż”.
 */
export type AkcjaFlagi = 'zapisz' | 'wylacz' | 'odrzuc' | 'wlacz';

export function akcjaFlagi(s: StanFlagi): AkcjaFlagi {
  if (!s.maZbior) return 'zapisz';
  if (s.zapalona) return 'wylacz';
  return s.pokazZapisz ? 'odrzuc' : 'wlacz';
}

/** Klucz tekstu podpowiedzi przy fladze (tooltip na pulpicie). */
export function kluczPodpowiedziFlagi(s: StanFlagi): string {
  if (!s.maZbior) return s.mozeZapisac ? 'search.my_filters.tip_save_as' : 'search.my_filters.tip_need_price';
  if (s.zapalona) return 'search.my_filters.tip_switch_off';
  // Przy wysuniętym „Zapisz” dotknięcie flagi odrzuca zmiany i wraca do zbioru (§1.6, Maciek 19.10).
  return s.pokazZapisz ? 'search.my_filters.tip_back_to_saved' : 'search.my_filters.tip_switch_on';
}

/** Klucz tekstu podpowiedzi przy „Zapisz”. */
export function kluczPodpowiedziZapisu(s: StanFlagi): string {
  if (!s.mozeZapisac) return 'search.my_filters.tip_need_price';
  return s.maZbior ? 'search.my_filters.tip_save_changes' : 'search.my_filters.tip_save_as';
}

/** Klucz jednorazowego dymka pod flagą (§1.8; przy niezapisanym zbiorze to dymek powitalny, §1.71). */
export function kluczDymka(s: StanFlagi): string {
  if (s.maZbior) return 'search.my_filters.hint_saved';
  return s.mozeZapisac ? 'search.my_filters.hint_unsaved' : 'search.my_filters.hint_unsaved_no_price';
}

/** Dymek pokazuje się raz: przy niezapisanym zbiorze albo gdy Moje filtry są na ekranie. */
export function pokazacDymek(s: StanFlagi, widziany: boolean): boolean {
  return !widziany && (!s.maZbior || s.zapalona);
}

/** Godziny od ostatniego obejrzenia zbioru — okno „+N nowych ofert”. `null` = nie liczyć. */
export function godzinOd(iso: string | null | undefined, teraz = Date.now()): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t) || t >= teraz) return null;
  return (teraz - t) / 3_600_000;
}

/** Zbiór zawężony do ofert opublikowanych od ostatniego obejrzenia (licznik „+N”). */
export function zbiorOdObejrzenia(zapisane: SourcePostFilters, godzin: number): SourcePostFilters {
  const juz = zapisane.postedSinceHours;
  return { ...zapisane, postedSinceHours: juz && juz < godzin ? juz : godzin };
}

/* ---------- Opis zbioru i różnica (tooltip, „Zapisano: …”) ---------- */

const KLUCZE_OPISANE = new Set(['areas', 'rentMin', 'rentMax', 'costMode', 'rooms', 'areaMin', 'areaMax']);

/** Ile filtrów zbioru NIE ma własnej części opisu (reszta: „+N filtry”). */
function ileInnych(f: SourcePostFilters): number {
  const n = fromSavedFiltersDoc(toSavedFiltersDoc(f));
  return Object.entries(n).filter(([k, v]) => !KLUCZE_OPISANE.has(k) && k !== 'postingTypes' && k !== 'sort' && v !== undefined && v !== null).length;
}

interface Czesci {
  lokalizacja: string;
  cena: string | null;
  pokoje: string | null;
  metraz: string | null;
}

function czesci(f: SourcePostFilters, t: TFunction, lang: string): Czesci {
  const n = fromSavedFiltersDoc(toSavedFiltersDoc(f));
  const miasto = t(`search.area.miasto_mianownik.${PRIMARY_ACTIVE_CITY}`, { defaultValue: PRIMARY_ACTIVE_CITY });
  const obszary = (n.areas ?? []).map((a) => etykietaObszaru(a, t));
  const cena = etykietaCenyZ(n, t, lang);
  return {
    lokalizacja: obszary.length ? obszary.join(', ') : t('search.my_filters.whole_city', { miasto }),
    cena: cena ? [cena.zakres, cena.dopisek].filter(Boolean).join(' ') : null,
    pokoje: etykietaWyboruPokoi(n.rooms, t),
    metraz: formatRange(t, n.areaMin, n.areaMax, 'm²', lang),
  };
}

/** „Mokotów, Ochota · do 3 500 zł · 2 pokoje · +2 filtry” — tooltip flagi i toast pierwszego zapisu. */
export function opisZbioru(f: SourcePostFilters, t: TFunction, lang: string): string {
  const c = czesci(f, t, lang);
  const inne = ileInnych(f);
  return [c.lokalizacja, c.cena, c.pokoje, c.metraz, inne > 0 ? t('search.my_filters.more_filters', { count: inne }) : null]
    .filter(Boolean)
    .join(' · ');
}

/** Najwięcej chipów w Ustawieniach, potem jeden „+ N” (makieta `settings-moje-filtry.html`). */
export const MAKS_CHIPOW_USTAWIEN = 5;

/**
 * Napisy pozostałych filtrów zbioru — TE SAME klucze i kolejność co chipy w rzędzie filtrów strony
 * wyników (`activeChips` w `SearchPage.tsx`), żeby Ustawienia nazywały filtr tak, jak widzi go
 * najemca w wynikach. Filtr bez napisu tutaj liczy się do „+ N”.
 */
function napisyInnych(n: SourcePostFilters, t: TFunction, lang: string): { napisy: string[]; bezNapisu: number } {
  const napisy: string[] = [];
  const opisane = new Set<string>(KLUCZE_OPISANE);
  const dodaj = (klucz: string, napis: string | null | false | undefined) => {
    opisane.add(klucz);
    if (napis) napisy.push(napis);
  };
  if (n.nearMetro) dodaj('nearMetro', t('search.filters.near_metro'));
  for (const p of n.poi ?? []) dodaj('poi', t(`search.filters.poi_${p}`));
  if (n.postedSinceHours) dodaj('postedSinceHours', t(`search.filters.posted_options.${n.postedSinceHours}`));
  if (n.zeZwierzeciem) dodaj('zeZwierzeciem', t('search.pets_chip'));
  if (n.umeblowanie) dodaj('umeblowanie', t(n.umeblowanie === 'umeblowane' ? 'search.filters.furnished_yes' : 'search.filters.furnished_no'));
  if (n.pietro) dodaj('pietro', t('search.filters.floor_ground'));
  if (n.kuchnia) dodaj('kuchnia', t(n.kuchnia === 'oddzielna' ? 'search.filters.chip_kitchen_separate' : 'search.filters.chip_kitchen_annex'));
  if (n.lazienki) dodaj('lazienki', t(n.lazienki === '1' ? 'search.filters.chip_baths_1' : 'search.filters.chip_baths_2plus'));
  if (n.balkon) dodaj('balkon', t('search.filters.balcony'));
  if (n.winda) dodaj('winda', t('search.filters.elevator'));
  if (n.internet) dodaj('internet', t('search.filters.chip_internet_fibre'));
  if (n.typBudynku) dodaj('typBudynku', t(`search.filters.building_type_value.${n.typBudynku}`));
  if (n.wiekBudynku) dodaj('wiekBudynku', t(`search.filters.building_age_value.${n.wiekBudynku}`));
  for (const u of n.udogodnienia ?? []) dodaj('udogodnienia', t(`search.filters.amenity.${u}`));
  if (n.garaz) dodaj('garaz', t('search.filters.chip_garage'));
  if (n.kaucjaMax != null) dodaj('kaucjaMax', t('search.filters.chip_deposit_max', { kwota: formatLiczby(n.kaucjaMax, lang) }));
  if (n.bezAdresu) dodaj('bezAdresu', t('search.filters.chip_no_address'));
  if (n.bezZdjec === false) dodaj('bezZdjec', t('search.filters.chip_only_with_photos'));
  if (n.wlascicielNaFlatHero) dodaj('wlascicielNaFlatHero', t('search.filters.owner_on_flathero'));
  if (n.availableNow) dodaj('availableNow', t('search.filters.available_now'));
  if (n.polygon && n.polygon.length >= 3) dodaj('polygon', t('search.draw.chip'));
  const bezNapisu = Object.entries(n)
    .filter(([k, v]) => !opisane.has(k) && k !== 'postingTypes' && k !== 'sort' && v !== undefined && v !== null).length;
  return { napisy, bezNapisu };
}

/**
 * „Oferty dobieramy według Twoich zapisanych filtrów:” w Ustawieniach (C6, §1.64; makieta
 * `settings-moje-filtry.html`): „Warszawa · Mokotów, Ochota”, „do 3 500 zł (bez opłat)”,
 * „2, 3 pokoje”, potem każdy pozostały filtr pod swoją nazwą z rzędu filtrów („Umeblowane”,
 * „Balkon lub taras”). Najwyżej `MAKS_CHIPOW_USTAWIEN` chipów, reszta to `wiecej` („+ 2”).
 */
export function chipyZbioru(f: SourcePostFilters, t: TFunction, lang: string): { chipy: string[]; wiecej: number } {
  const n = fromSavedFiltersDoc(toSavedFiltersDoc(f));
  const miasto = t(`search.area.miasto_mianownik.${PRIMARY_ACTIVE_CITY}`, { defaultValue: PRIMARY_ACTIVE_CITY });
  const obszary = (n.areas ?? []).map((a) => etykietaObszaru(a, t));
  const cena = etykietaCenyZ(n, t, lang);
  const inne = napisyInnych(n, t, lang);
  const wszystkie = [
    obszary.length ? `${miasto} · ${obszary.join(', ')}` : t('search.my_filters.whole_city', { miasto }),
    cena ? `${cena.zakres} ${cena.dopisek ?? t('settings.flat_alerts.rent_only')}` : null,
    etykietaWyboruPokoi(n.rooms, t),
    formatRange(t, n.areaMin, n.areaMax, 'm²', lang),
    ...inne.napisy,
  ].filter((x): x is string => !!x);
  return {
    chipy: wszystkie.slice(0, MAKS_CHIPOW_USTAWIEN),
    wiecej: Math.max(wszystkie.length - MAKS_CHIPOW_USTAWIEN, 0) + inne.bezNapisu,
  };
}

/** „Cena: do 3 500 zł → do 4 000 zł · Pokoje: dowolne → 2 pokoje” — toast kolejnego zapisu. */
export function roznicaZbiorow(a: SourcePostFilters, b: SourcePostFilters, t: TFunction, lang: string): string {
  const ca = czesci(a, t, lang);
  const cb = czesci(b, t, lang);
  const out: string[] = [];
  const jedna = (nazwa: string, x: string | null, y: string | null, brak: string) => {
    if (x !== y) out.push(t('search.my_filters.diff_item', { name: nazwa, from: x ?? brak, to: y ?? brak }));
  };
  jedna(t('search.filters.price'), ca.cena, cb.cena, t('search.my_filters.any_price'));
  jedna(t('search.filters.rooms'), ca.pokoje, cb.pokoje, t('search.my_filters.any_rooms'));
  jedna(t('search.filters.area'), ca.metraz, cb.metraz, t('search.my_filters.any_area'));
  jedna(t('search.my_filters.location'), ca.lokalizacja, cb.lokalizacja, '');
  // Reszta filtrów nie ma krótkiej etykiety — wystarczy powiedzieć, że się zmieniła.
  const resztaA = kluczZbioru({ ...a, areas: undefined, suburbs: undefined, rentMin: undefined, rentMax: undefined, costMode: undefined, rooms: undefined, areaMin: undefined, areaMax: undefined });
  const resztaB = kluczZbioru({ ...b, areas: undefined, suburbs: undefined, rentMin: undefined, rentMax: undefined, costMode: undefined, rooms: undefined, areaMin: undefined, areaMax: undefined });
  if (resztaA !== resztaB) out.push(t('search.my_filters.diff_other'));
  return out.join(' · ');
}

/* ---------- Panel filtrów: pastylka „Moje filtry” i pole „Zapisz…” nad „Pokaż N ofert” (C2, plan E1.4, §1.4) ---------- */

export interface StanPanelu {
  /** Pastylka aktywna — jest zapisany zbiór; bez niego wyszarzona z „Nie masz jeszcze zapisanych filtrów” (§1.71). */
  pigulkaAktywna: boolean;
  /** Szkic panelu = zbiór (ta sama reguła co flaga, tylko dla szkicu). */
  pigulkaZapalona: boolean;
  /** Pole „Zapisz…” widać, gdy szkic różni się od zbioru (przed pierwszym zapisem: zawsze). */
  pokazPole: boolean;
  /** Pole da się zaznaczyć — w szkicu jest cena (§1.3). */
  poleAktywne: boolean;
  /** Pole zaznaczone naprawdę: wyszarzone albo schowane nigdy nie zapisuje. */
  zaznaczone: boolean;
}

export function stanPanelu({ zapisane, szkic, zaznaczone }: { zapisane: SourcePostFilters | null; szkic: SourcePostFilters; zaznaczone: boolean }): StanPanelu {
  const pigulkaAktywna = zapisane !== null;
  const pigulkaZapalona = pigulkaAktywna && rowneZbiorowi(szkic, zapisane);
  const pokazPole = !pigulkaZapalona;
  const poleAktywne = maCene(szkic);
  return { pigulkaAktywna, pigulkaZapalona, pokazPole, poleAktywne, zaznaczone: zaznaczone && pokazPole && poleAktywne };
}

/** Podpowiedź pastylki — ta sama treść co przy fladze (§1.6). */
export function kluczPodpowiedziPigulki(s: StanPanelu): string {
  if (!s.pigulkaAktywna) return 'search.my_filters.tip_none_saved';
  return s.pigulkaZapalona ? 'search.my_filters.tip_switch_off' : 'search.my_filters.tip_switch_on';
}

/** Etykieta pola nad „Pokaż N ofert”: przed pierwszym zapisem „Zapisz jako moje filtry” (§1.71). */
export function kluczPolaZapisu(maZbior: boolean): string {
  return maZbior ? 'search.my_filters.tip_save_changes' : 'search.my_filters.tip_save_as';
}

/* ---------- Pytanie o e-maile po pierwszym zapisie (C3, plan E1.5, §1.5, §1.34) ---------- */

/**
 * Czy po tym zapisie zadać jednorazowe pytanie o e-maile. „ODPOWIEDZIANE” NIE MA WŁASNEJ KOLUMNY:
 * pytanie należy do przejścia `saved_at` null → ustawione (pierwszy zapis), a to zdarza się na
 * koncie raz — „Cofnij” pierwszego zapisu było w komunikacie, który pytanie zastępuje (§1.25).
 *  - Pole e-mail zaznaczone w onboardingu (§1.34) ustawia `saved_at` już tam: na stronie wyników
 *    nie ma pierwszego zapisu, więc nie ma pytania.
 *  - Przełącznik w Ustawieniach (§1.64) istnieje tylko przy zapisanym zbiorze (bez niego jest
 *    wyłączony), więc dotknięty znaczy, że pierwszy zapis już był.
 *  - Na wszelki wypadek: włączone już powiadomienia to też odpowiedź.
 * Zamknięcie pytania bez odpowiedzi też je zużywa („pytamy raz”); powiadomienia zostają wyłączone.
 */
export function pytacOMaile(przed: { savedAt: string | null }, po: { savedAt: string | null; alerts: { enabled: boolean } }): boolean {
  return przed.savedAt === null && po.savedAt !== null && !po.alerts.enabled;
}
